import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { copyToClipboard, formatCurrency } from 'src/utils';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { LinkComponent } from 'src/components';
import { priceColumn } from 'src/utils/fast-moving-products';
import { SelectedColumn } from 'src/types';
import useResolution from 'src/hooks/useResolution';
import { BigCalendar } from './BigCalender';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { useFastMovingProducts } from 'src/hooks/fast-moving-products';
import { MobileDataTable } from './MobileDataTable';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ClearIcon from '@material-ui/icons/Clear';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import { orderBy } from 'lodash';

const useStyles = makeStyles({
  tableContainer: {
    marginTop: 16,
    border: '1px solid black',
    maxHeight: 600,
    overflow: 'auto'
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    minWidth: 800
  },
  tableCell: {
    border: '1px solid black',
    padding: 8,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    zIndex: 0
  },
  tableHeaderCell: {
    border: '2px solid black',
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 12
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#e0e0e0'
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#ffffff'
    },
    '&:hover': {
      background: '#bdbdbd',

      // Ensure sticky column gets the same hover effect
      '& $stickyColumn': {
        background: '#bdbdbd'
      }
    }
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    zIndex: 11,
    backgroundColor: '#f0f0f0',
    border: '1px solid black',

    // Ensure hover applies from row hover
    '&:hover, $tableRow:hover &': {
      background: '#bdbdbd'
    }
  },
  stickyHeaderColumn: {
    position: 'sticky',
    left: 0,
    top: 0,
    zIndex: 99,
    backgroundColor: '#f0f0f0',
    borderBottom: '2px solid gray'
  }
});

interface Props {
  headers?: string[];
  fastMovingData?: any[];
  columnToShow?: SelectedColumn[];
  columnTotal?: any;
  queryParams?: {
    [k: string]: string;
  };
}

const Component: FC<Props> = ({
  fastMovingData,
  columnToShow,
  columnTotal,
  queryParams
}) => {
  const classes = useStyles();
  const { xs, lg } = useResolution();
  const { getSoldTimelineParams } = useFastMovingProducts();
  const [customFastMovingData, setcustomFastMovingData] = useState<any>();
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  //this is highly focus on defining the keys for each column
  const filteredShowColumns = useMemo(() => {
    if (!columnToShow || columnToShow.length === 0) {
      return ['product_name'];
    }

    const names = columnToShow.map((col) => col.name || '').filter(Boolean);

    const getPriority = (key: string): number => {
      if (key.endsWith('_sold')) return 1;
      if (key.endsWith('_available')) return 2;
      return 3;
    };

    names.sort((a, b) => {
      const pa = getPriority(a);
      const pb = getPriority(b);
      return pa === pb ? a.localeCompare(b) : pa - pb;
    });

    return ['product_name', ...names];
  }, [columnToShow]);

  const onHandleCopyProductName = (productName?: string) => {
    copyToClipboard(productName || '');
  };

  const viewSoldTimeline = (product_id?: string) => {
    setIsCalendarOpen(true);

    const payload: any = {
      product_id,
      action: undefined,
      category_id: undefined
    };

    const preset = queryParams?.preset_dates;
    const dateFrom = queryParams?.date_sold_from;
    const dateTo = queryParams?.date_sold_to;

    if (preset) {
      payload.preset_date = preset;
      payload.from_date = undefined;
      payload.to_date = undefined;
    } else if (dateFrom || dateTo) {
      payload.preset_date = undefined;
      payload.from_date = dateFrom;
      payload.to_date = dateTo;
    }
    getSoldTimelineParams(payload);
  };

  const handleFastMovingCellValue = (
    header: string,
    data: any
  ): string | number => {
    const value = data[header];

    if (header.toLowerCase() === 'sku' && !value) {
      return 'N/A';
    }

    if (priceColumn.includes(header)) {
      return formatCurrency(value ?? 0);
    }

    if (header === 'margin_percentage') {
      return `${value ?? 0}%`;
    }

    return value ?? 0;
  };

  useEffect(() => {
    let isMounted = true;

    if (fastMovingData) {
      const updateFastMovingData = fastMovingData.map((item) => {
        const soldKeys = filteredShowColumns?.filter(
          (key) => key.endsWith('_sold') && key in item && key !== 'total_sold'
        );
        const availableKeys = filteredShowColumns?.filter(
          (key) =>
            key.endsWith('_available') &&
            key in item &&
            key !== 'total_available'
        );

        const totalSold = soldKeys.reduce(
          (sum, key) => sum + (item[key] || 0),
          0
        );
        const totalAvailable = availableKeys.reduce(
          (sum, key) => sum + (item[key] || 0),
          0
        );

        return {
          ...item,
          total_sold: totalSold,
          total_available: totalAvailable
        };
      });

      if (isMounted) {
        setcustomFastMovingData(updateFastMovingData);
      }
    }

    return () => {
      isMounted = false; // cleanup
    };
  }, [columnToShow, fastMovingData, filteredShowColumns]);

  const calculatedTotals = useMemo(() => {
    const data = customFastMovingData || [];

    // Define only the properties you want to sum
    //This works only on production keys
    const includedProperties = [
      'earnshaw_manila_sold',
      'lpc_alabang_zapote_sold',
      'pc_worth_experience_qc_main_sold',
      'total_sold',
      'earnshaw_manila_available',
      'lpc_alabang_zapote_available',
      'pc_worth_experience_qc_main_available',
      'total_available'
    ];

    const totals: { [key: string]: number } = {};

    includedProperties.forEach((prop) => {
      totals[prop] = 0;
    });

    data.forEach((item: any) => {
      includedProperties.forEach((prop) => {
        const value = item[prop];
        if (value !== undefined && !isNaN(parseFloat(value))) {
          totals[prop] += parseFloat(value);
        }
      });
    });

    return totals;
  }, [customFastMovingData]);

  columnTotal(calculatedTotals);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    column: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const handleSortSelection = (
    column: string,
    direction: 'asc' | 'desc' | ''
  ) => {
    if (!customFastMovingData) return;

    const sortedData = direction
      ? orderBy(
          customFastMovingData,
          [
            (row) => {
              const value = row[column];
              if (value === null || value === undefined)
                return direction === 'asc' ? Infinity : -Infinity;

              if (typeof value === 'number') return value;

              if (typeof value === 'string') {
                const numericValue =
                  typeof value === 'string'
                    ? parseFloat(value.replace(/[₱,]/g, ''))
                    : value;

                if (column === 'product_name') {
                  return value.toLowerCase();
                }

                if (!isNaN(numericValue)) return numericValue;
              }
            }
          ],
          [direction]
        )
      : [...(fastMovingData || [])];

    setcustomFastMovingData(sortedData);
    handleCloseMenu();
  };

  return (
    <>
      {xs && filteredShowColumns ? (
        <MobileDataTable
          filteredShowColumns={filteredShowColumns}
          calculatedTotals={calculatedTotals}
          customFastMovingData={customFastMovingData}
          onHandleViewDate={(product_id: string) => {
            viewSoldTimeline(product_id);
          }}
        />
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            {calculatedTotals && (
              <TableBody>
                <TableRow
                  // className={classes.totalRow}
                  style={{ background: '#b0bec5' }}
                >
                  {filteredShowColumns?.map((header, index) => (
                    <TableCell
                      key={index}
                      align={index === 0 ? 'left' : 'right'}
                      className={`${classes.tableCell} ${
                        !lg && index === 0 ? classes.stickyColumn : ''
                      }`}
                      style={{ fontWeight: 'bold' }}
                    >
                      {index === 0
                        ? 'TOTALS'
                        : // @ts-ignore-next-line
                          calculatedTotals[header] || 0}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            )}

            <TableHead>
              <TableRow>
                {filteredShowColumns?.map((header, index) => (
                  <TableCell
                    key={header}
                    className={`${classes.tableHeaderCell} ${
                      !lg && index === 0 ? classes.stickyHeaderColumn : ''
                    }`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, header)}
                        style={{ cursor: 'pointer' }}
                      >
                        <ImportExportIcon
                          fontSize="inherit"
                          style={{ color: 'black' }}
                        />
                      </IconButton>
                      {header?.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {customFastMovingData?.map((data: any, rowIndex: number) => (
                <TableRow key={rowIndex} className={classes.tableRow}>
                  {filteredShowColumns?.map((header: any, colIndex) => (
                    <TableCell
                      className={`${classes.tableCell} ${
                        !lg && colIndex === 0 ? classes.stickyColumn : ''
                      }`}
                      key={colIndex}
                    >
                      {header === 'product_name' ? (
                        <Box
                          display={'flex'}
                          style={{
                            textAlign: 'left',
                            columnGap: '.5em',
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            minWidth: 500,
                            maxWidth: 500
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal'
                            }}
                          >
                            <FileCopyIcon
                              style={{ cursor: 'pointer', marginRight: '5px' }}
                              onClick={() =>
                                onHandleCopyProductName(data[header])
                              }
                            />
                            <Box>
                              <LinkComponent
                                openInNewTab={true}
                                href={`/app/products/${data?.product_id}`}
                                title={`${data[header] ?? '--'}`}
                              />
                            </Box>
                          </div>
                          <Tooltip title={'View sold items timeline'}>
                            <CalendarTodayIcon
                              style={{ cursor: 'pointer', marginRight: '5px' }}
                              onClick={() => viewSoldTimeline(data?.product_id)}
                            />
                          </Tooltip>
                        </Box>
                      ) : (
                        <>{handleFastMovingCellValue(header, data)}</>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <BigCalendar
        isOpen={isCalendarOpen}
        onHandleClose={() => setIsCalendarOpen(false)}
      />

      {/* Sort Options Menu */}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
        <MenuItem
          onClick={() => handleSortSelection(activeColumn || '', 'asc')}
        >
          <ArrowUpwardIcon fontSize="small" style={{ marginRight: 6 }} />
          Ascending
        </MenuItem>
        <MenuItem
          onClick={() => handleSortSelection(activeColumn || '', 'desc')}
        >
          <ArrowDownwardIcon fontSize="small" style={{ marginRight: 6 }} />
          Descending
        </MenuItem>
        <MenuItem onClick={() => handleSortSelection(activeColumn || '', '')}>
          <ClearIcon fontSize="small" style={{ marginRight: 6 }} />
          Clear Sort
        </MenuItem>
      </Menu>
    </>
  );
};

export const FastMovingProductTableV2 = memo(Component);
